import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SliderAssessment.css';

const SliderAssessment = ({ 
  performanceQuestions = [], 
  onScoreUpdate, 
  initialScores = {},
  disabled = false,
  areaComments = {},
  onAreaCommentChange,
  onCreateAssessment
}) => {
  const [scores, setScores] = useState(initialScores);
  const [baselineScores, setBaselineScores] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [skippedAreas, setSkippedAreas] = useState({});
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const touchStartX = useRef(0);
  const areaCommentsRef = useRef(areaComments);
  const scoresRef = useRef(scores);
  const preVoiceTextRef = useRef('');
  const voiceTargetRef = useRef(null);
  const onAreaCommentChangeRef = useRef(onAreaCommentChange);
  const onScoreUpdateRef = useRef(onScoreUpdate);

  // Keep refs in sync with latest values
  useEffect(() => { areaCommentsRef.current = areaComments; }, [areaComments]);
  useEffect(() => { scoresRef.current = scores; }, [scores]);
  useEffect(() => { onAreaCommentChangeRef.current = onAreaCommentChange; }, [onAreaCommentChange]);
  useEffect(() => { onScoreUpdateRef.current = onScoreUpdate; }, [onScoreUpdate]);

  // Capture baseline scores when initialScores loads
  useEffect(() => {
    setScores(initialScores);
    const baseline = {};
    Object.entries(initialScores).forEach(([key, val]) => {
      if (key !== 'overall_comments' && val && typeof val === 'object') {
        baseline[key] = val.score || 0;
      }
    });
    setBaselineScores(baseline);
  }, [initialScores]);

  // Reset to first step when questions change
  useEffect(() => { setCurrentStep(0); }, [performanceQuestions]);

  // --- Score handlers ---
  const handleSliderChange = (questionId, value) => {
    const newScore = parseFloat(value);
    const updatedScores = { ...scores, [questionId]: { ...scores[questionId], score: newScore } };
    setScores(updatedScores);
    if (onScoreUpdate) onScoreUpdate(questionId, 'score', newScore);
  };

  const handleManualInput = (questionId, value, maxScore) => {
    let raw = value.replace(/[^0-9.]/g, '');
    if (raw === '' || raw === '.') {
      setScores({ ...scores, [questionId]: { ...scores[questionId], score: raw === '' ? 0 : scores[questionId]?.score || 0, _inputVal: raw } });
      return;
    }
    let num = parseFloat(raw);
    if (isNaN(num)) num = 0;
    if (num > maxScore) num = maxScore;
    if (num < 0) num = 0;
    num = Math.round(num * 10) / 10;
    const updatedScores = { ...scores, [questionId]: { ...scores[questionId], score: num, _inputVal: undefined } };
    setScores(updatedScores);
    if (onScoreUpdate) onScoreUpdate(questionId, 'score', num);
  };

  const handleStep = (questionId, delta, maxScore) => {
    const current = scores[questionId]?.score || 0;
    let next = Math.round((current + delta) * 10) / 10;
    if (next < 0) next = 0;
    if (next > maxScore) next = maxScore;
    const updatedScores = { ...scores, [questionId]: { ...scores[questionId], score: next, _inputVal: undefined } };
    setScores(updatedScores);
    if (onScoreUpdate) onScoreUpdate(questionId, 'score', next);
  };

  const handleAreaCommentChange = (areaName, comments) => {
    if (onAreaCommentChange) onAreaCommentChange(areaName, comments);
  };

  // --- Voice-to-text ---
  const startVoice = (targetType, targetName) => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Voice input not supported. Try Chrome or Edge.');
      return;
    }

    // Stop if already listening
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      recognitionRef.current = null;
      setIsListening(false);
      return;
    }

    // Capture context at mic start
    voiceTargetRef.current = { type: targetType, name: targetName };
    if (targetType === 'area') {
      preVoiceTextRef.current = areaCommentsRef.current[targetName] || '';
    } else {
      preVoiceTextRef.current = scoresRef.current.overall_comments || '';
    }

    const SpeechRecog = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SpeechRecog();
    recog.continuous = false;
    recog.interimResults = true;
    recog.lang = 'en-US';

    recog.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      transcript = transcript.trim();
      if (!transcript) return;

      const pre = preVoiceTextRef.current;
      const combined = pre ? pre + ' ' + transcript : transcript;
      const target = voiceTargetRef.current;

      if (target && target.type === 'area') {
        if (onAreaCommentChangeRef.current) onAreaCommentChangeRef.current(target.name, combined);
      } else {
        setScores(prev => ({ ...prev, overall_comments: combined }));
        if (onScoreUpdateRef.current) onScoreUpdateRef.current('overall_comments', 'comments', combined);
      }
    };

    recog.onerror = (e) => {
      console.error('Speech recognition error:', e.error);
      recognitionRef.current = null;
      setIsListening(false);
    };

    recog.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognitionRef.current = recog;
    setIsListening(true);
    recog.start();
  };

  // Stop voice on step change
  useEffect(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      recognitionRef.current = null;
      setIsListening(false);
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Helpers ---
  const getScoreLabel = (score) => {
    if (score >= 9) return 'Excellent';
    if (score >= 8) return 'Very Good';
    if (score >= 7) return 'Good';
    if (score >= 6) return 'Satisfactory';
    if (score >= 5) return 'Average';
    if (score >= 4) return 'Below Avg';
    if (score >= 3) return 'Needs Work';
    if (score >= 2) return 'Poor';
    return 'Not Scored';
  };

  const getScoreColor = (score) => {
    if (score >= 8) return '#28a745';
    if (score >= 6) return '#28a745';
    if (score >= 4) return '#ffc107';
    if (score >= 2) return '#fd7e14';
    return '#dc3545';
  };

  const groupQuestionsByArea = (questions) => {
    return questions.reduce((groups, question) => {
      const area = question.area_name.toLowerCase();
      if (!groups[area]) groups[area] = [];
      groups[area].push(question);
      return groups;
    }, {});
  };

  const getAreaIcon = (areaName) => {
    const icons = {
      'batting': '\u{1F3CF}', 'bowling': '\u{1F3AF}', 'fielding': '\u{1F94E}',
      'wicket keeping': '\u{1F9E4}', 'wicketkeeping': '\u{1F9E4}', 'wicket-keeping': '\u{1F9E4}',
      'fitness': '\u{1F4AA}', 'attitude': '\u{1F60A}', 'overall': '\u{1F3C6}',
      'running': '\u{1F3C3}', 'agility': '\u{26A1}', 'endurance': '\u{1F525}',
      'technique': '\u{2699}\u{FE0F}', 'strategy': '\u{1F9E0}', 'teamwork': '\u{1F91D}',
      'leadership': '\u{1F451}', 'game awareness': '\u{1F9ED}', 'decision making': '\u{1F914}'
    };
    return icons[areaName?.toLowerCase().trim()] || '\u{1F3CF}';
  };

  const getAreaColor = (areaName) => {
    const colors = {
      'batting': '#dc3545', 'bowling': '#007bff', 'fielding': '#fd7e14',
      'fitness': '#28a745', 'attitude': '#6f42c1', 'overall': '#495057',
      'wicketkeeping': '#17a2b8'
    };
    return colors[areaName?.toLowerCase()] || '#6c757d';
  };

  const getAreaDisplayName = (areaName) => {
    const names = {
      'batting': 'Batting', 'bowling': 'Bowling', 'fielding': 'Fielding',
      'fitness': 'Fitness', 'attitude': 'Attitude', 'overall': 'Overall',
      'wicketkeeping': 'Wicketkeeping'
    };
    return names[areaName?.toLowerCase()] || areaName;
  };

  const getAreaDescription = (areaName) => {
    const descs = {
      'batting': 'Technique, timing, shot selection & footwork',
      'bowling': 'Action, accuracy, line & length & variations',
      'fielding': 'Catching, throwing, ground fielding & positioning',
      'fitness': 'Endurance, agility, strength & flexibility',
      'attitude': 'Behavior, focus, teamwork & commitment',
      'wicketkeeping': 'Stance, glove work, stumping & collection'
    };
    return descs[areaName?.toLowerCase()] || '';
  };

  // --- Build steps ---
  const groupedQuestions = groupQuestionsByArea(performanceQuestions);
  const areaNames = Object.keys(groupedQuestions);

  const steps = [];
  areaNames.forEach(areaName => {
    groupedQuestions[areaName].forEach(q => {
      steps.push({ type: 'question', areaName, question: q });
    });
    steps.push({ type: 'area-comment', areaName });
  });
  steps.push({ type: 'overall-comment' });

  const totalSteps = steps.length;
  const step = steps[currentStep] || steps[0];

  // --- Navigation ---
  const navigate = useCallback((dir) => {
    setCurrentStep(prev => {
      const next = prev + dir;
      if (next < 0 || next >= totalSteps) return prev;
      return next;
    });
  }, [totalSteps]);

  const jumpToArea = (areaName) => {
    const idx = steps.findIndex(s => s.areaName === areaName && s.type === 'question');
    if (idx >= 0) setCurrentStep(idx);
  };

  const skipArea = (areaName) => {
    setSkippedAreas(prev => ({ ...prev, [areaName]: true }));
    // Find the next area's first question, or go to overall comment
    const currentAreaIdx = areaNames.indexOf(areaName);
    if (currentAreaIdx < areaNames.length - 1) {
      const nextArea = areaNames[currentAreaIdx + 1];
      const idx = steps.findIndex(s => s.areaName === nextArea && s.type === 'question');
      if (idx >= 0) setCurrentStep(idx);
    } else {
      // Last area, go to overall comment
      const idx = steps.findIndex(s => s.type === 'overall-comment');
      if (idx >= 0) setCurrentStep(idx);
    }
  };

  const unskipArea = (areaName) => {
    setSkippedAreas(prev => {
      const next = { ...prev };
      delete next[areaName];
      return next;
    });
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Touch swipe
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 60) navigate(diff > 0 ? 1 : -1);
  };

  if (!step) return null;

  // --- Computed values ---
  const activeArea = step.areaName || areaNames[areaNames.length - 1] || '';
  const areaQuestions = step.areaName ? (groupedQuestions[step.areaName] || []) : [];
  const areaSteps = steps.filter(s => s.areaName === step.areaName);
  const posInArea = areaSteps.indexOf(step);
  const qPosInArea = step.type === 'question'
    ? areaSteps.filter((s, i) => i <= posInArea && s.type === 'question').length
    : null;

  const getAreaAvg = (areaName) => {
    const qs = groupedQuestions[areaName] || [];
    if (!qs.length) return 0;
    return qs.reduce((acc, q) => acc + (scores[q.id]?.score || 0), 0) / qs.length;
  };

  return (
    <form className="slider-assessment" onSubmit={(e) => e.preventDefault()}>
      {/* ===== Header with area tabs ===== */}
      <div className="sa-header">
        <h3>Performance Assessment</h3>
        <div className="area-tabs-row">
          {areaNames.map(name => {
            const avg = getAreaAvg(name);
            return (
              <button
                key={name}
                type="button"
                className={`area-tab ${name === activeArea ? 'active' : ''} ${skippedAreas[name] ? 'skipped' : ''}`}
                onClick={() => jumpToArea(name)}
                style={{ '--tab-color': getAreaColor(name) }}
              >
                <span className="tab-icon">{getAreaIcon(name)}</span>
                <span className="tab-name">{getAreaDisplayName(name)}</span>
                {skippedAreas[name] 
                  ? <span className="tab-badge skipped-badge">Skipped</span>
                  : <span className="tab-badge" style={{ background: getScoreColor(avg) }}>{avg.toFixed(1)}</span>
                }
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== Progress bar ===== */}
      <div className="overall-progress">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }} />
        </div>
        <span className="progress-text">{currentStep + 1} / {totalSteps}</span>
      </div>

      {/* ===== Step info + dots ===== */}
      {step.areaName && (
        <div className="step-info">
          <span className="step-area" style={{ color: getAreaColor(step.areaName) }}>
            {getAreaIcon(step.areaName)} {getAreaDisplayName(step.areaName)}
          </span>
          <span className="step-counter">
            {step.type === 'question' ? `Question ${qPosInArea} of ${areaQuestions.length}` : 'Coach Notes'}
          </span>
          {/* Skip / Unskip Section Button */}
          {step.type === 'question' && qPosInArea === 1 && !skippedAreas[step.areaName] && (
            <button
              type="button"
              className="skip-section-btn"
              onClick={() => skipArea(step.areaName)}
              title={`Skip ${getAreaDisplayName(step.areaName)} section`}
            >
              ⏭ Skip {getAreaDisplayName(step.areaName)}
            </button>
          )}
          {skippedAreas[step.areaName] && (
            <button
              type="button"
              className="unskip-section-btn"
              onClick={() => unskipArea(step.areaName)}
            >
              ↩ Unskip {getAreaDisplayName(step.areaName)}
            </button>
          )}
          <div className="step-dots">
            {areaSteps.map((s, i) => (
              <span
                key={i}
                className={`step-dot ${i === posInArea ? 'current' : ''} ${i < posInArea ? 'done' : ''}`}
                onClick={() => { const idx = steps.indexOf(s); if (idx >= 0) setCurrentStep(idx); }}
                style={{ '--dot-color': getAreaColor(step.areaName) }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ===== Card stage (animated) ===== */}
      <div
        key={currentStep}
        className="card-stage"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* --- Question Card --- */}
        {step.type === 'question' && (() => {
          const q = step.question;
          const currentScore = scores[q.id]?.score || 0;
          const inputVal = scores[q.id]?._inputVal;
          const baseline = baselineScores[q.id];
          const hasBaseline = baseline !== undefined && baseline > 0;
          const scoreDiff = hasBaseline ? currentScore - baseline : 0;

          return (
            <div className="q-card">
              <div className="q-question">{q.question_text}</div>

              <div className="q-score-ring" style={{ borderColor: getScoreColor(currentScore) }}>
                <span className="q-score-num" style={{ color: getScoreColor(currentScore) }}>
                  {currentScore.toFixed(1)}
                </span>
                <span className="q-score-lbl">{getScoreLabel(currentScore)}</span>
              </div>

              <div className="q-slider-wrap">
                <span className="q-slider-end">0</span>
                <input
                  type="range"
                  min="0"
                  max={q.max_score}
                  step="0.1"
                  value={currentScore}
                  onChange={(e) => handleSliderChange(q.id, e.target.value)}
                  className="q-slider"
                  disabled={disabled}
                  style={{
                    background: `linear-gradient(to right, #dc3545 0%, #fd7e14 25%, #ffc107 50%, #28a745 75%, #28a745 100%)`
                  }}
                />
                <span className="q-slider-end">{q.max_score}</span>
              </div>

              <div className="q-stepper">
                <button type="button" className="q-step-btn"
                  onClick={() => handleStep(q.id, -0.1, q.max_score)}
                  disabled={disabled || currentScore <= 0}
                  aria-label="Decrease score">{'\u2212'}</button>
                <input
                  type="text"
                  inputMode="decimal"
                  className="q-step-val"
                  value={inputVal !== undefined ? inputVal : currentScore.toFixed(1)}
                  onChange={(e) => handleManualInput(q.id, e.target.value, q.max_score)}
                  onBlur={() => {
                    if (scores[q.id]?._inputVal !== undefined) {
                      const s = { ...scores };
                      delete s[q.id]._inputVal;
                      setScores(s);
                    }
                  }}
                  disabled={disabled}
                  style={{ color: getScoreColor(currentScore) }}
                />
                <button type="button" className="q-step-btn"
                  onClick={() => handleStep(q.id, 0.1, q.max_score)}
                  disabled={disabled || currentScore >= q.max_score}
                  aria-label="Increase score">+</button>
              </div>

              {hasBaseline && (
                <div className="q-baseline">
                  <span className="q-baseline-label">Previous:</span>
                  <span className="q-baseline-val">{baseline.toFixed(1)}</span>
                  {scoreDiff !== 0 && (
                    <span className={`q-baseline-diff ${scoreDiff > 0 ? 'up' : 'down'}`}>
                      {scoreDiff > 0 ? '\u25B2' : '\u25BC'} {Math.abs(scoreDiff).toFixed(1)}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* --- Area Comment Card --- */}
        {step.type === 'area-comment' && (
          <div className="comment-card">
            <div className="comment-icon">{'\u{1F4AC}'}</div>
            <h3>{getAreaDisplayName(step.areaName)} {'\u2014'} Coach Notes</h3>
            <p className="comment-hint">{getAreaDescription(step.areaName)}</p>
            <div className="comment-field">
              <textarea
                value={areaComments[step.areaName] || ''}
                onChange={(e) => handleAreaCommentChange(step.areaName, e.target.value)}
                placeholder={`Share observations about ${getAreaDisplayName(step.areaName).toLowerCase()} performance...`}
                rows="4"
                disabled={disabled}
              />
              <button
                type="button"
                className={`mic-btn ${isListening ? 'active' : ''}`}
                onClick={() => startVoice('area', step.areaName)}
                disabled={disabled}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                {isListening ? '\u23F9' : '\u{1F3A4}'}
              </button>
            </div>
            {isListening && <div className="voice-indicator">Listening...</div>}
          </div>
        )}

        {/* --- Overall Comment Card --- */}
        {step.type === 'overall-comment' && (
          <div className="comment-card overall">
            <div className="comment-icon">{'\u{1F3C6}'}</div>
            <h3>Overall Assessment</h3>
            <p className="comment-hint">Final summary, recommendations, and development goals</p>
            <div className="comment-field">
              <textarea
                value={scores.overall_comments || ''}
                onChange={(e) => {
                  const text = e.target.value;
                  setScores(prev => ({ ...prev, overall_comments: text }));
                  if (onScoreUpdate) onScoreUpdate('overall_comments', 'comments', text);
                }}
                placeholder="Overall observations and recommendations..."
                rows="5"
                disabled={disabled}
              />
              <button
                type="button"
                className={`mic-btn ${isListening ? 'active' : ''}`}
                onClick={() => startVoice('overall', null)}
                disabled={disabled}
              >
                {isListening ? '\u23F9' : '\u{1F3A4}'}
              </button>
            </div>
            {isListening && <div className="voice-indicator">Listening...</div>}
          </div>
        )}
      </div>

      {/* ===== Navigation ===== */}
      <div className="sa-nav">
        <button type="button" className="sa-nav-btn" onClick={() => navigate(-1)} disabled={currentStep <= 0}>
          {'\u2190'} Prev
        </button>
        <div className="sa-nav-hint">Swipe or use arrow keys</div>
        {currentStep >= totalSteps - 1 ? (
          <button type="button" className="sa-nav-btn create-assessment"
            onClick={onCreateAssessment} disabled={disabled}>
            ✅ Create Assessment
          </button>
        ) : (
          <button type="button" className={`sa-nav-btn primary`}
            onClick={() => navigate(1)}>
            Next {'\u2192'}
          </button>
        )}
      </div>
    </form>
  );
};

export default SliderAssessment;
